/**
 * useCompactBodyState — the single source of the compact popover's body
 * dispatch state (prim #1 §1). Extracted from `CompactVqeContent` so the
 * scrolling body and the PINNED footer/preview read ONE derivation of
 * `showOpenFull` + the live SQL preview instead of each recomputing it.
 *
 * It owns:
 *  • the live `CompactQueryModel` (off the shared edit context / Resolver),
 *  • the sliced `preview` SQL text (always the base statement, source of truth),
 *  • the WHERE-subquery drill dispatch (active frame + derived `drilledCtx` +
 *    the base-depth subquery wiring),
 *  • the `nestedDegraded` flag a drilled `CompactNestedSections` reports up,
 *  • and the derived `showOpenFull` (false whenever a card — DDL, top-level
 *    degrade, or a nested degrade — already owns the screen's single CTA, §1.5).
 *
 * The dispatch-chain branch order MUST match `CompactVqeContent`'s render chain
 * (drill → update → delete → degrade → select) so `topLevelDegradeVisible` is
 * true EXACTLY when the top-level `CompactDegradeNotice` renders.
 */

import { useMemo, useState } from 'react';

import type { ParseOptions } from '../../../sql-grammar/model';
import type { QueryStructure } from '../../../sql-resolver/types/queryStructure';
import type { VqeEditContext } from '../edit/editContext';
import { deriveSubqueryEditContext } from '../edit/deriveSubqueryEditContext';
import { resolveDrilledSubqueryBodyEnd } from '../render/select/resolveDrilledSubqueryBodyEnd';
import type { SubqueryWiring } from '../render/select/SelectRenderer';
import type { RenderMode } from '../types';
import { vqeCopy } from '../ux/vqeCopy';
import type { DdlCardModel } from './compactDdlCard';
import { buildCompactPreview } from './compactLivePreview';
import { hasWindowFunction } from './compactDegradeDetail';
import { buildCompactSubqueryCtx } from './sections/CompactNestedSections';
import type { CompactDrill } from './useCompactDrill';
import { useCompactQueryModel, type CompactQueryModel } from './useCompactQueryModel';

/**
 * ANA-06 — the truthful "why is Open full editor offered" hint for the compact
 * footer's standing CTA. Non-empty ONLY in the per-column-locked case: the
 * editable statement carries a window function (whose chip locks read-only via
 * `SelectList`'s `lockWindowColumns`) AND the footer owns the CTA (`showOpenFull`
 * — i.e. the statement is NOT degraded). A NAMED window instead full-degrades
 * (`showOpenFull` false), so its "why" is the degrade card's own copy, never
 * this hint. Pure so it's unit-testable without a hook harness.
 */
export function compactOpenFullWindowHint(
  structure: QueryStructure | null,
  showOpenFull: boolean,
): string | undefined {
  return showOpenFull && structure && hasWindowFunction(structure)
    ? vqeCopy.capability.window.compactLockedHint
    : undefined;
}

export interface UseCompactBodyStateArgs {
  readonly editContext: VqeEditContext | null;
  readonly mode: RenderMode | null;
  readonly options: ParseOptions;
  /** DDL summary-card model (non-null ⇒ the DDL card wins the whole dispatch). */
  readonly ddlCard: DdlCardModel | null;
  readonly drill: CompactDrill;
  readonly tabId: string;
  /** The single "Open full editor" hand-off, threaded into the subquery wiring. */
  readonly onOpenFull: () => void;
}

export interface CompactBodyState {
  readonly model: CompactQueryModel;
  /** Sliced live SQL of the base cursor statement (buffer is the source of truth). */
  readonly preview: string;
  /** The active drilled subquery frame, or null when not drilled. */
  readonly active: CompactDrill['active'];
  /** Edit context of the deepest drilled subquery body, or null. */
  readonly drilledCtx: VqeEditContext | null;
  /** Base-depth WHERE-subquery drill wiring (undefined without an edit context). */
  readonly baseSubqueryCtx: SubqueryWiring | undefined;
  /** Whether the DDL summary card wins the dispatch (owns its own CTA). */
  readonly ddlCardVisible: boolean;
  /** Whether the TOP-LEVEL degrade notice renders (owns the CTA). */
  readonly topLevelDegradeVisible: boolean;
  /** Whether a DRILLED subquery is showing its own degrade card. */
  readonly nestedDegraded: boolean;
  readonly setNestedDegraded: (degraded: boolean) => void;
  /** True when the pinned footer should render its standing "Open full editor". */
  readonly showOpenFull: boolean;
  /**
   * ANA-06 — truthful tooltip for the footer's "Open full editor" CTA when the
   * editable statement has an inline window column locked read-only here;
   * `undefined` otherwise (a degrade card owns the "why", or there's no window).
   */
  readonly openFullWindowHint: string | undefined;
}

export function useCompactBodyState({
  editContext,
  mode,
  options,
  ddlCard,
  drill,
  tabId,
  onOpenFull,
}: UseCompactBodyStateArgs): CompactBodyState {
  const model = useCompactQueryModel(editContext, mode, options);
  const preview = buildCompactPreview(model.bufferText, model.span);

  // When drilled, derive the edit context for the deepest subquery body. Offsets
  // are absolute, so we re-resolve the (shifted) end off the live buffer and
  // derive directly from the base context (intermediate frames only set depth).
  const active = drill.active;
  const drilledCtx = useMemo<VqeEditContext | null>(() => {
    if (!editContext || !active) return null;
    const source =
      editContext.service.getParsedBuffer(editContext.documentId)?.parsedDocument.source ?? '';
    const end = resolveDrilledSubqueryBodyEnd(source, active.bodyStart, options) ?? active.bodyEnd;
    return deriveSubqueryEditContext(editContext, editContext.statementId, active.bodyStart, end);
  }, [editContext, active, options]);

  // Base WHERE-subquery drill wiring (depth 0); FROM/JOIN subqueries stay inert.
  const baseSubqueryCtx = editContext
    ? buildCompactSubqueryCtx(editContext.statementId, tabId, drill, onOpenFull)
    : undefined;

  const [nestedDegraded, setNestedDegraded] = useState(false);

  // Mirror the render chain's branch order (drill → update → delete → degrade →
  // select) so this is true EXACTLY when the top-level `CompactDegradeNotice`
  // renders — never in the drill/update/delete branches.
  const topLevelDegradeVisible =
    !(active && drilledCtx) &&
    !(mode === 'update' && model.updateOutline) &&
    !(mode === 'delete' && model.deleteOutline) &&
    (model.degrade.degraded || !model.outline);

  const ddlCardVisible = !!ddlCard;

  // § 1.5 — a DDL card, a top-level degrade card, or a nested degrade card each
  // owns the screen's single primary CTA, so the footer must hide its own copy.
  const showOpenFull = !(ddlCardVisible || topLevelDegradeVisible || nestedDegraded);

  return {
    model,
    preview,
    active,
    drilledCtx,
    baseSubqueryCtx,
    ddlCardVisible,
    topLevelDegradeVisible,
    nestedDegraded,
    setNestedDegraded,
    showOpenFull,
    openFullWindowHint: compactOpenFullWindowHint(model.structure, showOpenFull),
  };
}
